'use client'

/**
 * LIVE auth surface — wires the shared Papr funnel auth components to real NextAuth +
 * the /api/auth/* endpoints. The form markup is reproduced verbatim from the cached
 * design handoff (onb-auth.jsx / onb-registration.jsx); only the action wiring is real
 * and all preview-only "Demo ·" hints are removed. Hard navigations (window.location)
 * are used on success so middleware re-runs.
 */

import React from 'react'
import { signIn } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'

import { AuthSplit, VerifyStep } from './auth'
import { OB, FONT_MONO, Icon, OnbField, OnbInput, OnbPrimary, OnbGhost, OnbTag, Spinner } from './kit'
import { routes } from '@/lib/routes'
import { errorMessage } from '@/lib/utils/api-error'

const EMAIL_RE = /.+@.+\..+/

/** Map raw NextAuth credential errors to friendly copy (keeping verification prompts). */
function humanize(err: string): string {
  if (/verify your email/i.test(err)) return err
  if (!err || err === 'CredentialsSignin') return 'Invalid email or password.'
  return err
}

// ===========================================================================
// Sign in
// ===========================================================================

export function LiveSignIn() {
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl') || routes.admin

  const [email, setEmail] = React.useState('')
  const [pw, setPw] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [sub, setSub] = React.useState<'form' | 'verify'>('form')

  const emailOk = EMAIL_RE.test(email)
  const canSubmit = emailOk && pw.length >= 1

  async function submit() {
    if (!canSubmit || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await signIn('credentials', { email, password: pw, redirect: false, callbackUrl })
      if (r?.error) {
        setError(humanize(r.error))
        setBusy(false)
      } else if (r?.ok) {
        window.location.href = callbackUrl
      } else {
        setError('Could not sign you in.')
        setBusy(false)
      }
    } catch {
      setError('An unexpected error occurred. Please try again.')
      setBusy(false)
    }
  }

  async function sendLink() {
    if (!emailOk) {
      setError('Enter your email first.')
      return
    }
    setError(null)
    await fetch('/api/auth/otp/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    setSub('verify')
  }

  const topRight = (
    <>
      <span>New to Papr?</span>
      <a href={routes.register} style={{ color: OB.ink, fontWeight: 600, textDecoration: 'none' }}>Create an account</a>
    </>
  )

  return (
    <AuthSplit stepKey={sub} topRight={topRight}>
      {sub === 'verify' ? (
        <VerifyStep
          email={email}
          firstName="there"
          demoHint={false}
          successText="Signing you in…"
          onBack={() => setSub('form')}
          onResend={async () => {
            await fetch('/api/auth/otp/send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email }),
            })
          }}
          onVerify={async (code: string) => {
            const r = await fetch('/api/auth/otp/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, code }),
            })
            const j = await r.json().catch(() => ({}) as { success?: boolean; error?: string })
            if (!r.ok || !j.success) throw new Error(errorMessage(j.error, 'Invalid or expired code.'))
            const s = await signIn('credentials', {
              email,
              password: '__OTP_VERIFIED__' + code,
              redirect: false,
              callbackUrl,
            })
            if (s?.error) throw new Error(errorMessage(s.error, 'Could not sign you in.'))
            if (!s?.ok) throw new Error('Could not sign you in.')
          }}
          onVerified={() => {
            window.location.href = callbackUrl
          }}
        />
      ) : (
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.035em', color: OB.ink, margin: 0, lineHeight: 1.08 }}>Welcome back</h1>
          <p style={{ fontSize: 14.5, color: OB.mute, margin: '11px 0 26px', lineHeight: 1.5 }}>Sign in to pick up where you left off.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <OnbField label="Work email">
              <OnbInput value={email} onChange={setEmail} placeholder="you@studio.com" type="email" autoFocus size="lg"
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') void submit() }} />
            </OnbField>
            <OnbField label="Password">
              <OnbInput value={pw} onChange={setPw} placeholder="••••••••" type="password" size="lg"
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') void submit() }} />
            </OnbField>
            <div style={{ marginTop: -8, textAlign: 'right' }}>
              <a href={routes.forgotPassword} style={{ fontSize: 12.5, color: OB.mute, fontWeight: 600, textDecoration: 'none' }}>Forgot your password?</a>
            </div>
            <OnbPrimary full size="lg" arrow={!busy} onClick={() => void submit()} disabled={!canSubmit || busy}>
              {busy ? <Spinner /> : 'Sign in'}
            </OnbPrimary>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <span style={{ flex: 1, height: 1, background: OB.line2 }}></span>
            <span style={{ fontSize: 11.5, color: OB.dim, fontFamily: FONT_MONO }}>or</span>
            <span style={{ flex: 1, height: 1, background: OB.line2 }}></span>
          </div>

          <OnbGhost full size="lg" onClick={() => void sendLink()}>
            <Icon d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" size={17} color={OB.ink} />
            Email me a code
          </OnbGhost>
          {error && (
            <>
              <div style={{ height: 12 }}></div>
              <p style={{ fontSize: 12, color: '#b91c1c', margin: 0, lineHeight: 1.55, textAlign: 'center' }}>{error}</p>
            </>
          )}
        </div>
      )}
    </AuthSplit>
  )
}

// ===========================================================================
// Register
// ===========================================================================

export function LiveRegister() {
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [pw, setPw] = React.useState('')
  const [method, setMethod] = React.useState<'link' | 'password'>('link')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [sub, setSub] = React.useState<'form' | 'verify'>('form')

  const emailOk = EMAIL_RE.test(email)
  const canContinue = name.trim().length > 1 && emailOk && (method === 'link' || pw.length >= 6)
  const firstName = name.trim().split(' ')[0] || 'there'

  function buildBody(): { name: string; email: string; password?: string; confirmPassword?: string } {
    const body: { name: string; email: string; password?: string; confirmPassword?: string } = { name: name.trim(), email }
    if (method === 'password') {
      body.password = pw
      body.confirmPassword = pw
    }
    return body
  }

  async function submit() {
    if (!canContinue || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildBody()),
      })
      const j = await r.json().catch(() => ({}) as { success?: boolean; error?: string })
      if (!r.ok || !j.success) {
        setError(errorMessage(j.error, 'Could not create your account.'))
        setBusy(false)
        return
      }
      setBusy(false)
      setSub('verify')
    } catch {
      setError('An unexpected error occurred. Please try again.')
      setBusy(false)
    }
  }

  const topRight = (
    <>
      <span>Already with us?</span>
      <a href={routes.signin} style={{ color: OB.ink, fontWeight: 600, textDecoration: 'none' }}>Sign in</a>
    </>
  )

  return (
    <AuthSplit stepKey={sub} topRight={topRight}>
      {sub === 'verify' ? (
        <VerifyStep
          email={email}
          firstName={firstName}
          demoHint={false}
          successText="Verified — taking you to setup…"
          onBack={() => setSub('form')}
          onResend={async () => {
            await fetch('/api/auth/register', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(buildBody()),
            })
          }}
          onVerify={async (code: string) => {
            const r = await fetch('/api/auth/register/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, code }),
            })
            const j = await r.json().catch(() => ({}) as { success?: boolean; error?: string })
            if (!r.ok || !j.success) throw new Error(errorMessage(j.error, 'Invalid or expired code.'))
            const s = await signIn('credentials', {
              email,
              password: '__OTP_VERIFIED__' + code,
              redirect: false,
            })
            if (s?.error) throw new Error(errorMessage(s.error, 'Could not sign you in.'))
            if (!s?.ok) throw new Error('Could not sign you in.')
          }}
          onVerified={() => {
            window.location.href = routes.onboarding
          }}
        />
      ) : (
        <div>
          <div style={{ display: 'inline-flex', marginBottom: 22 }}>
            <OnbTag color={OB.ink} bg="rgba(202,255,51,0.5)">Free · no card needed</OnbTag>
          </div>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.035em', color: OB.ink, margin: 0, lineHeight: 1.08 }}>Create your account</h1>
          <p style={{ fontSize: 14.5, color: OB.mute, margin: '11px 0 26px', lineHeight: 1.5 }}>
            Start sending invoices in minutes. Papr drafts, sends, and chases them so you don’t have to.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <OnbField label="Your name">
              <OnbInput value={name} onChange={setName} placeholder="Alex Rivera" autoFocus size="lg" />
            </OnbField>
            <OnbField label="Work email">
              <OnbInput value={email} onChange={setEmail} placeholder="you@studio.com" type="email" size="lg"
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' && method === 'link') void submit() }} />
            </OnbField>

            {/* Method segmented control */}
            <div style={{ display: 'flex', gap: 6, padding: 4, background: 'rgba(0,0,0,0.04)', borderRadius: 12 }}>
              {([
                { k: 'link' as const, label: 'Email me a link', sub: 'Fastest' },
                { k: 'password' as const, label: 'Set a password', sub: null },
              ]).map((o) => {
                const on = method === o.k
                return (
                  <button key={o.k} onClick={() => setMethod(o.k)} className="onb-seg"
                    style={{ flex: 1, height: 42, borderRadius: 9, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      background: on ? '#fff' : 'transparent', boxShadow: on ? '0 1px 0 rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.05)' : 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                      fontSize: 13, fontWeight: on ? 600 : 500, color: on ? OB.ink : OB.mute }}>
                    {o.label}
                    {o.sub && on && <span style={{ fontSize: 10, color: OB.greenInk, background: 'rgba(16,185,129,0.12)', padding: '2px 6px', borderRadius: 999, fontWeight: 600, fontFamily: FONT_MONO }}>{o.sub}</span>}
                  </button>
                )
              })}
            </div>

            {method === 'password' && (
              <div className="onb-step">
                <OnbField label="Password" hint="At least 6 characters">
                  <OnbInput value={pw} onChange={setPw} placeholder="••••••••" type="password" size="lg"
                    onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') void submit() }} />
                </OnbField>
              </div>
            )}

            <OnbPrimary full size="lg" arrow={!busy} onClick={() => void submit()} disabled={!canContinue || busy}>
              {busy ? <Spinner /> : (method === 'link' ? 'Email me a sign-in link' : 'Create account')}
            </OnbPrimary>
          </div>

          {error && <p style={{ fontSize: 12, color: '#b91c1c', margin: '14px 0 0', lineHeight: 1.55, textAlign: 'center' }}>{error}</p>}

          <p style={{ fontSize: 11.5, color: OB.dim, margin: '18px 0 0', lineHeight: 1.55, textAlign: 'center' }}>
            By continuing you agree to Papr’s <a href={routes.terms} style={{ color: OB.mute }}>Terms</a> &amp; <a href={routes.privacy} style={{ color: OB.mute }}>Privacy Policy</a>.
          </p>
        </div>
      )}
    </AuthSplit>
  )
}

// ===========================================================================
// Forgot password
// ===========================================================================

export function LiveForgot() {
  const [email, setEmail] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [sent, setSent] = React.useState(false)
  const [resent, setResent] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const emailOk = EMAIL_RE.test(email)

  async function submit() {
    if (!emailOk || busy) return
    setBusy(true)
    setError(null)
    try {
      await fetch('/api/auth/reset-password/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      setSent(true)
    } catch {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const topRight = (
    <>
      <span>Remembered it?</span>
      <a href={routes.signin} style={{ color: OB.ink, fontWeight: 600, textDecoration: 'none' }}>Back to sign in</a>
    </>
  )

  return (
    <AuthSplit stepKey={sent ? 'sent' : 'form'} topRight={topRight}>
      {sent ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: OB.ink, margin: '0 auto 22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" size={26} color={OB.lime} />
          </div>
          <h1 style={{ fontSize: 27, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Check your inbox</h1>
          <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>
            We sent a password reset link to <strong style={{ color: OB.ink, fontWeight: 600 }}>{email}</strong>. It expires in 1 hour.
          </p>
          <div style={{ marginTop: 16, fontSize: 12.5, color: OB.dim }}>
            Didn’t get it?{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); void submit(); setResent(true) }} style={{ color: OB.ink, fontWeight: 600, textDecoration: 'none' }}>Resend email</a>
            {resent && <span style={{ color: OB.greenInk, marginLeft: 8, fontFamily: FONT_MONO }}>✓ sent</span>}
          </div>
        </div>
      ) : (
        <div>
          <div style={{ width: 52, height: 52, borderRadius: 15, background: OB.ink, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 8H9V6a3 3 0 016 0v3z" size={24} color={OB.lime} />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Reset your password</h1>
          <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>
            Enter the email you signed up with and we’ll send a secure link to set a new password.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <OnbField label="Work email">
              <OnbInput value={email} onChange={setEmail} placeholder="you@studio.com" type="email" autoFocus size="lg"
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') void submit() }} />
            </OnbField>
            <OnbPrimary full size="lg" arrow={!busy} onClick={() => void submit()} disabled={!emailOk || busy}>
              {busy ? <Spinner /> : 'Send reset link'}
            </OnbPrimary>
          </div>
          {error && <p style={{ fontSize: 12, color: '#b91c1c', margin: '14px 0 0', lineHeight: 1.55 }}>{error}</p>}
        </div>
      )}
    </AuthSplit>
  )
}

// ===========================================================================
// Reset password (set new)
// ===========================================================================

export function LiveResetPassword() {
  const searchParams = useSearchParams()
  const token = searchParams.get('token')

  const [status, setStatus] = React.useState<'loading' | 'valid' | 'invalid'>('loading')
  const [pw, setPw] = React.useState('')
  const [confirm, setConfirm] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [done, setDone] = React.useState(false)

  React.useEffect(() => {
    if (!token) {
      setStatus('invalid')
      return
    }
    let cancelled = false
    const run = async () => {
      try {
        const r = await fetch(`/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`)
        if (cancelled) return
        setStatus(r.ok ? 'valid' : 'invalid')
      } catch {
        if (!cancelled) setStatus('invalid')
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [token])

  const reqs = [
    { label: 'At least 8 characters', ok: pw.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(pw) },
    { label: 'One number', ok: /\d/.test(pw) },
  ]
  const score = reqs.filter((r) => r.ok).length
  const match = confirm.length > 0 && pw === confirm
  const canSave = score === 3 && match
  const strengthColor = ['#ef4444', '#ef4444', '#f59e0b', OB.greenInk][score]

  async function save() {
    if (!canSave || busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await fetch('/api/auth/reset-password/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: pw, confirmPassword: confirm }),
      })
      const j = await r.json().catch(() => ({}) as { success?: boolean; error?: string })
      if (!r.ok || !j.success) {
        setError(errorMessage(j.error, 'Could not reset your password.'))
        setBusy(false)
        return
      }
      setBusy(false)
      setDone(true)
    } catch {
      setError('An unexpected error occurred. Please try again.')
      setBusy(false)
    }
  }

  const stepKey = done ? 'done' : status

  return (
    <AuthSplit stepKey={stepKey}>
      {done ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: OB.green, margin: '0 auto 22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={28} color="#fff" />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Password updated</h1>
          <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>
            You’re all set. Sign in with your new password to get back to work.
          </p>
          <OnbPrimary full size="lg" arrow onClick={() => { window.location.href = routes.signin }}>Continue to sign in</OnbPrimary>
        </div>
      ) : status === 'loading' ? (
        <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spinner size={24} />
        </div>
      ) : status === 'invalid' ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: 15, background: '#fef2f2', border: '1px solid rgba(239,68,68,0.25)', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" size={24} color="#ef4444" />
          </div>
          <h1 style={{ fontSize: 27, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Link no longer valid</h1>
          <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>
            This reset link is invalid or has expired.
          </p>
          <OnbPrimary full size="lg" arrow onClick={() => { window.location.href = routes.forgotPassword }}>Request a new link</OnbPrimary>
        </div>
      ) : (
        <div>
          <div style={{ width: 52, height: 52, borderRadius: 15, background: OB.ink, marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon d="M12 1a5 5 0 00-5 5v3H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2h-1V6a5 5 0 00-5-5zm3 8H9V6a3 3 0 016 0v3z" size={24} color={OB.lime} />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.03em', color: OB.ink, margin: 0 }}>Set a new password</h1>
          <p style={{ fontSize: 14, color: OB.mute, margin: '11px 0 24px', lineHeight: 1.55 }}>Choose a strong password you don’t use anywhere else.</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <OnbField label="New password">
              <OnbInput value={pw} onChange={setPw} placeholder="••••••••" type="password" autoFocus size="lg" />
            </OnbField>
            {pw.length > 0 && (
              <div>
                <div style={{ display: 'flex', gap: 5, marginBottom: 9 }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{ flex: 1, height: 4, borderRadius: 999, background: i < score ? strengthColor : 'rgba(0,0,0,0.08)', transition: 'background .2s' }}></span>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px 14px' }}>
                  {reqs.map((r, i) => (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: r.ok ? OB.greenInk : OB.dim }}>
                      <span style={{ width: 14, height: 14, borderRadius: '50%', background: r.ok ? 'rgba(16,185,129,0.14)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {r.ok && <Icon d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" size={9} color={OB.greenInk} />}
                      </span>{r.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <OnbField label="Confirm password">
              <OnbInput value={confirm} onChange={setConfirm} placeholder="••••••••" type="password" size="lg"
                onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter') void save() }} />
            </OnbField>
            {confirm.length > 0 && !match && <div style={{ fontSize: 11.5, color: '#ef4444', marginTop: -6 }}>Passwords don’t match yet.</div>}
            <OnbPrimary full size="lg" arrow={!busy} onClick={() => void save()} disabled={!canSave || busy}>
              {busy ? <Spinner /> : 'Save new password'}
            </OnbPrimary>
          </div>
          {error && <p style={{ fontSize: 12, color: '#b91c1c', margin: '14px 0 0', lineHeight: 1.55 }}>{error}</p>}
        </div>
      )}
    </AuthSplit>
  )
}
